<script setup lang="ts">
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import ErrorBar from "./ErrorBar.vue";
import useStore from "../store.ts";
import {
  ENV_URL_STORAGE_KEY,
  getPageEnvironmentUrl,
  getStoredEnvironmentUrl,
  normalizeEnvironmentUrl,
  syncEnvironmentUrlToNativeUpdater,
} from "../utils/EnvironmentUrl.ts";
import {getStoredDeviceId, storeDeviceId} from "../utils/DeviceId.ts";
import {version} from "../../package.json";
import logoUrl from "../assets/infologic-logo.svg";

const ENV_HOST_EXAMPLE = "de.example.com";

const deviceId = ref(getStoredDeviceId() ?? "");
const envHost = ref("");
const envUrlTouched = ref(false);
const router = useRouter();

/* served over https ⇒ the backend is that origin (same rule as SocketManager); otherwise ask */
const envUrlRequired = computed(() => getPageEnvironmentUrl() === null);

if (envUrlRequired.value) {
  const stored = getStoredEnvironmentUrl();
  if (stored) {
    const parsed = new URL(stored);
    envHost.value = parsed.protocol === "https:" ? parsed.host : stored;
  }
}

const normalizedEnvUrl = computed(() => normalizeEnvironmentUrl(envHost.value));
const envUrlIsValid = computed(() => !envUrlRequired.value || normalizedEnvUrl.value !== null);

const handleSubmit = () => {
  storeDeviceId(deviceId.value);
  const targetPath = `/?dev=${encodeURIComponent(deviceId.value)}`;

  if (envUrlRequired.value) {
    envUrlTouched.value = true;
    const normalized = normalizedEnvUrl.value;
    if (!normalized) return;

    const previous = getStoredEnvironmentUrl();
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ENV_URL_STORAGE_KEY, normalized);
    }
    void syncEnvironmentUrlToNativeUpdater(normalized);

    if (previous !== normalized && typeof window !== "undefined") {
      // Resolve through the router: a root-absolute "/?dev=" drops index.html on file:// (Tizen widget, hash history).
      const target = new URL(router.resolve(targetPath).href, window.location.href);
      const hashOnly = target.href.split("#")[0] === window.location.href.split("#")[0];
      window.location.assign(target.href);
      // A fragment-only change doesn't reload; the socket must restart against the new environment URL.
      if (hashOnly) window.location.reload();
      return;
    }
  }

  router.push(targetPath);
}
const store = useStore();
</script>

<template>
  <ErrorBar v-if="store.error" :message="store.error" :visible="store.error.length == 0"/>
  <div class="auth-page">
    <img class="auth-brand" :src="logoUrl" alt="Infologic"/>

    <div class="auth-form-wrap">
      <form class="auth-form" @submit.prevent="handleSubmit">
        <h1>Connect this display</h1>
        <p class="auth-sub">Enter the device ID below to link this display to the Lumids Display Engine.</p>

        <label class="field">
          <span class="field-lbl">Device ID</span>
          <input
            v-model="deviceId"
            class="input"
            type="text"
            placeholder="e.g. RPI-CLIENT-1"
            autocomplete="off"
            required
          />
        </label>

        <label v-if="envUrlRequired" class="field">
          <span class="field-lbl">Environment</span>
          <input
            v-model="envHost"
            class="input"
            type="text"
            inputmode="url"
            :placeholder="ENV_HOST_EXAMPLE"
            autocomplete="off"
            spellcheck="false"
            :required="envUrlRequired"
            @blur="envUrlTouched = true"
          />
          <span v-if="envUrlTouched && !envUrlIsValid" class="field-error">
            Enter the environment's host or URL, e.g. {{ ENV_HOST_EXAMPLE }} or 192.168.1.10:8080
          </span>
          <span v-else class="field-hint">
            Will connect to {{ normalizedEnvUrl ?? `https://${ENV_HOST_EXAMPLE}` }}
          </span>
        </label>

        <button
          :disabled="deviceId.length === 0 || (envUrlRequired && !envUrlIsValid)"
          class="btn btn-primary btn-block"
          type="submit"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m10 17 5-5-5-5"/><path d="M15 12H3"/><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
          </svg>
          Connect
        </button>

        <div class="auth-meta">Version {{ version }}</div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.auth-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: var(--muted);
  color: var(--ink);
  font-family: var(--ui);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 28px;
}

.auth-brand {
  position: absolute;
  top: 28px;
  left: 30px;
  width: 130px;
  height: 36px;
  object-fit: contain;
  object-position: left center;
}

.auth-form-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-form {
  width: 300px;
  display: grid;
  gap: 16px;
}

.auth-form h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink);
}

.auth-sub {
  margin: -8px 0 6px;
  font-size: 0.8125rem;
  font-weight: 300;
  color: var(--ink-3);
  line-height: 1.5;
}

.field {
  display: grid;
  gap: 6px;
  text-align: left;
}

.field-lbl {
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
}

.field-hint {
  font-size: 11px;
  font-weight: 300;
  color: var(--ink-4);
}

.field-error {
  font-size: 11px;
  color: var(--err);
}

.input {
  height: 36px;
  width: 100%;
  border-radius: var(--r-sm);
  border: 1px solid var(--input);
  background: var(--bg);
  color: var(--ink);
  padding: 0 12px;
  font: inherit;
  font-size: 14px;
  box-shadow: var(--shadow-sm);
  transition: border-color .15s var(--ease), box-shadow .15s var(--ease);
}

.input::placeholder {
  color: var(--ink-4);
}

.input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.btn {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--r-sm);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  border: 1px solid transparent;
  transition: background .15s var(--ease), border-color .15s var(--ease);
}

.btn svg {
  width: 16px;
  height: 16px;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: var(--shadow-sm);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-700);
}

.btn-primary:disabled {
  background: var(--ink-4);
  cursor: not-allowed;
  box-shadow: none;
}

.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

.btn-block {
  width: 100%;
  margin-top: 4px;
}

.auth-meta {
  font-size: 12px;
  font-weight: 300;
  color: var(--ink-4);
  margin-top: 8px;
}
</style>
